import { useState } from "react";
import { copySelection, pasteClipboard, type ClipboardData } from "../model/inp/clipboard.ts";
import type { ElementRef } from "../model/inp/properties.ts";
import type { SwmmProject } from "../model/inp/types.ts";
import type { StoredProject } from "../model/storage.ts";

/**
 * In-memory only (never the OS/browser clipboard) — a snapshot of rows taken
 * at Ctrl+C time, independent of any later edit to the originals. Copies
 * `multiSelected` when non-empty, else `selected` alone.
 */
export function useClipboard(
  loaded: StoredProject | null,
  project: SwmmProject | null,
  selected: ElementRef | null,
  multiSelected: ElementRef[],
  mutateRawUndoable: (
    fn: (raw: ReadonlyMap<string, string[][]>) => ReadonlyMap<string, string[][]>
  ) => void,
  setSelected: (ref: ElementRef | null) => void,
  setMultiSelected: (refs: ElementRef[]) => void
) {
  const [clipboard, setClipboard] = useState<ClipboardData | null>(null);

  function handleCopy() {
    if (!loaded || !project) return;
    const selection = multiSelected.length > 0 ? multiSelected : selected ? [selected] : [];
    if (selection.length === 0) return;
    setClipboard(copySelection(loaded.rawSections, project, selection));
  }

  /** Undoable, unlike copy itself (read-only) — pasting creates new elements. The pasted elements become the new selection. */
  function handlePaste() {
    if (!loaded || !project || !clipboard || clipboard.entries.length === 0) return;
    const result = pasteClipboard(loaded.rawSections, project, clipboard);
    mutateRawUndoable(() => result.raw);
    setSelected(result.pastedRefs[0] ?? null);
    setMultiSelected(result.pastedRefs);
  }

  function clear() {
    setClipboard(null);
  }

  return { clipboard, handleCopy, handlePaste, clear };
}
